import {
  InputBase,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { SearchIcon } from "components/icons";
import { useAskHelenaConversations } from "controller";
import dayjs from "dayjs";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef } from "react";
import type { AskHelenaConversation } from "shared";
// Explicit path, not the `utilz/helpers` barrel: that exports a DIFFERENT
// `useDebounce` (a function debouncer). This is the value debouncer.
import { useDebounce } from "utilz/helpers/hooks/useDebounceHook";
import { recordChatOpened, scrubQueryText, trackAiHome } from "../../analytics";
import { chatSearchQueryAtom, openAskHelenaConversation } from "../store";
import { askHelenaConversationTitle } from "../utils/askHelenaConversationList";

/**
 * Every keystroke is a query that may scan message text, so the term settles
 * before it is sent. React Query cancels the superseded request when the key
 * changes, so no AbortController is needed here.
 */
const SEARCH_DEBOUNCE_MS = 300;

const PAGE_SIZE = 20;

/**
 * The panel shows one page and has no pager, so there is no stale page to reset
 * when the term changes — the classic "searching from page 2 lands on an empty
 * page" bug is impossible here by construction. Add the reset alongside any
 * pager that gets added later.
 */
const FIRST_PAGE = 1;

const SKELETON_ROWS = [0, 1, 2, 3, 4];

/**
 * Chat history search. Matching happens server-side, case-insensitively, over
 * the title AND the text of the messages — which is the point: a thread is
 * found by a word the user remembers typing, even when its title never
 * mentioned it. A blank box is the full list.
 */
export const ChatSearchPanel = () => {
  const theme = useTheme();
  // Seeded from the atom so a reload of `?q=<term>` lands on the same results.
  const searchQuery = useAtomValue(chatSearchQueryAtom);
  const setSearchQuery = useSetAtom(chatSearchQueryAtom);
  const [debouncedQuery, setQuery, query] = useDebounce(
    searchQuery,
    SEARCH_DEBOUNCE_MS,
  );
  const openChat = useSetAtom(openAskHelenaConversation);

  const term = debouncedQuery.trim();

  // The URL follows the settled term, not every keystroke — the debounce that
  // guards the request guards the history entry too.
  useEffect(() => {
    setSearchQuery(debouncedQuery);
  }, [debouncedQuery, setSearchQuery]);

  const { data, isLoading, isError, isFetching, isPreviousData, refetch } =
    useAskHelenaConversations({
      page: FIRST_PAGE,
      pageSize: PAGE_SIZE,
      search: term,
    });

  const results = data?.items ?? [];
  const trackedTerm = useRef<string | null>(null);

  useEffect(() => {
    if (!term || isFetching || isPreviousData) return;
    if (trackedTerm.current === term) return;

    trackedTerm.current = term;
    const scrubbed = scrubQueryText(term);
    trackAiHome("ai_chat_searched", {
      search_text: scrubbed.text,
      search_text_scrubbed: scrubbed.wasScrubbed,
      results_count: results.length,
    });
  }, [term, isFetching, isPreviousData, results.length]);

  const handleOpenChat = (conversation: AskHelenaConversation) => {
    recordChatOpened();
    trackAiHome("ai_chat_opened_from_history", {
      chat_id: conversation.id,
      source: "search",
      chat_age_days: dayjs().diff(dayjs(conversation.createdAt), "day"),
    });
    openChat(conversation.id);
  };

  const content = () => {
    if (isLoading) {
      return SKELETON_ROWS.map((row) => (
        <Skeleton
          key={row}
          variant="text"
          sx={{ mx: "8px", fontSize: "40px" }}
        />
      ));
    }

    if (isError) {
      return (
        <Stack sx={{ gap: "4px", p: "16px 8px" }}>
          <Typography
            variant="body-medium"
            sx={{ color: theme.palette.lightBg.low }}
          >
            Couldn't search your chats.
          </Typography>
          <Typography
            component="button"
            type="button"
            variant="body-medium"
            onClick={() => void refetch()}
            sx={{
              alignSelf: "flex-start",
              p: 0,
              border: "none",
              backgroundColor: "transparent",
              color: theme.palette.primary.tint,
              cursor: "pointer",
            }}
          >
            Retry
          </Typography>
        </Stack>
      );
    }

    if (results.length === 0) {
      return (
        <Typography
          variant="body-medium"
          sx={{ color: theme.palette.lightBg.low, p: "16px 8px" }}
        >
          {term ? `No chats match "${term}"` : "No chats yet."}
        </Typography>
      );
    }

    // Rendered in the order received — the server returns pinned threads first
    // here too.
    return results.map((conversation) => (
      <Stack
        key={conversation.id}
        component="button"
        type="button"
        direction="row"
        onClick={() => handleOpenChat(conversation)}
        sx={{
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          p: "12px 8px",
          border: "none",
          borderRadius: "4px",
          textAlign: "left",
          cursor: "pointer",
          backgroundColor: "transparent",
          ":hover": {
            backgroundColor: theme.palette.neutral["005"],
          },
        }}
      >
        <Typography
          variant="body-small"
          sx={{ color: theme.palette.lightBg.high }}
          noWrap
        >
          {askHelenaConversationTitle(conversation)}
        </Typography>
        <Typography
          variant="body-small"
          sx={{ color: theme.palette.lightBg.low, flexShrink: 0 }}
        >
          {dayjs(conversation.updatedAt).format("MMM DD")}
        </Typography>
      </Stack>
    ));
  };

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        maxWidth: "830px",
        mx: "auto",
        gap: "8px",
        p: "24px 70px",
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: "12px",
          p: "8px 16px",
          borderRadius: "8px",
          border: `1px solid ${theme.palette.primitive.system.violet[200]}`,
          backgroundColor: theme.palette.primitive.system.violet[25],
        }}
      >
        <SearchIcon
          sx={{ fontSize: "16px", color: theme.palette.lightBg.low }}
        />
        <InputBase
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search chat"
          inputProps={{ "aria-label": "Search chat" }}
          sx={{
            ...theme.typography["body-small"],
            flex: 1,
            color: theme.palette.lightBg.high,
          }}
        />
      </Stack>

      <Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: "8px 0" }}>
        {content()}
      </Stack>
    </Stack>
  );
};
